import type { AppRole } from '../auth/rolePermissions';

/**
 * Frontend mirror of the backend role ladder (roleMiddleware.ROLE_LEVELS).
 * Higher number = more permissions. Used for "minimum role" gating in the UI
 * (e.g. who can create quotes — REQ-02).
 */
// Phase D (2026-08-06, mig 126): 'reviewer' retired — review rights are
// skill-based (level >= 4). Numbers keep their historical values (gap at 2)
// so stored threshold settings stay stable.
export const ROLE_RANK: Record<string, number> = {
  pending: 0,
  reader: 1,
  technician: 3,
  admin: 4,
  superadmin: 5,
};

/** True when `role` meets or exceeds `minRole` (superadmin always passes). */
export function roleAtLeast(role: string, minRole: string): boolean {
  if (role === 'superadmin') return true;
  return (ROLE_RANK[role] ?? -1) >= (ROLE_RANK[minRole] ?? 99);
}

/** Roles selectable as a permission threshold (excludes 'pending'). */
export const THRESHOLD_ROLE_OPTIONS: { value: AppRole; label: string }[] = [
  { value: 'reader', label: 'Reader+' },
  { value: 'technician', label: 'Technician+' },
  { value: 'admin', label: 'Admin Only' },
  { value: 'superadmin', label: 'Superadmin Only' },
];

/** Threshold display label for a role (e.g. 'technician' → "Technician+"). */
export const roleThresholdLabel = (role: string): string =>
  THRESHOLD_ROLE_OPTIONS.find((o) => o.value === role)?.label ?? role;

/** Roles assignable as a quote-approval tier requirement. Tiers are retired
 *  (Phase B) — kept only for the read-only history view. */
export const APPROVAL_TIER_ROLE_OPTIONS = THRESHOLD_ROLE_OPTIONS.filter((o) =>
  (['technician', 'admin'] as AppRole[]).includes(o.value),
);

export interface RoleOption {
  value: AppRole;
  /** Display label shown in the User Management UI. */
  label: string;
  /** Accent colour for the role dot / avatar tint. */
  color: string;
}

/**
 * Canonical role picklist for the admin User Management UI — single source for
 * the label and accent colour of each role. Use this instead of re-declaring the
 * list per component (previously triplicated across UserTable / GalSearchInline /
 * PendingApprovalsBanner, each with inline hex literals).
 */
export const ROLE_OPTIONS: RoleOption[] = [
  { value: 'superadmin', label: 'Super Admin', color: '#7B1FA2' },
  { value: 'admin', label: 'Lab Admin', color: '#C62828' },
  { value: 'technician', label: 'Lab Technician', color: '#E65100' },
  { value: 'reader', label: 'Lab Reader', color: '#2E7D32' },
];

/** Look up a role's display option by its code. */
export const roleOption = (value: string): RoleOption | undefined =>
  ROLE_OPTIONS.find((r) => r.value === value);

/** Roles only a Super Admin may assign (mirrors backend ELEVATED_ROLES). */
export const ELEVATED_ROLE_VALUES: AppRole[] = ['superadmin', 'admin'];

/** sessionStorage key holding the impersonated role ("View as role" preview). */
export const IMPERSONATE_ROLE_STORAGE_KEY = 'belab-impersonate-role';

/** Request header carrying the impersonated role to the backend (downgrade-only,
 *  superadmin-only — see backend impersonationMiddleware and RBAC.md). */
export const IMPERSONATE_ROLE_HEADER = 'X-Impersonate-Role';
